import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    TextInput as NativeTextInput,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import {
    type DateTimePickerTriggerAccessibilityType,
    type DateTimePickerInputInstance,
    type DateTimePickerPickerType,
    type DateTimePickerValueType,
    type DateTimePickerDateRange,
    type IDateTimePickerRef,
    type DateCalendarType
} from "./type";
import type IDateTimePickerProps from "./type";
import stylesheet, {
    getDateTimePickerType,
    useStyles
} from "./stylesheet";
import {
    getIsSameDateTimePickerSelection,
    getDateTimePickerPlaceholderKey,
    getDateTimePickerTitleFormat,
    DATE_TIME_PICKER_EMPTY_VALUE,
    getDateTimePickerRangeError,
    getDateTimePickerRangeText,
    getDateTimePickerValueKey,
    getDateTimePickerDateText,
    getDateTimePickerResponse,
    getHasDateTimePickerValue,
    getDateTimePickerAnchor,
    copyDateTimePickerValue
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    type RefForwardingComponent,
    type NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    getAppearStyle,
    usePressScale,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    IBottomSheetRef
} from "../bottomSheet/type";
import type IFieldActionProps from "../fieldControl/internal/action/type";
import {
    type FieldInputAccessibilityType,
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../fieldControl/type";
import {
    Calendar as CalendarIcon,
    Clock as ClockIcon,
    Lock as LockIcon
} from "lucide-react-native";
import {
    type RRule
} from "rrule";
import * as fieldActionStylesheet from "../fieldControl/internal/action/stylesheet";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    parseRRuleConfig
} from "../../helpers/locale";
import {
    FieldClearIcon
} from "../fieldControl/icons";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import fieldStylesheet, {
    getFieldActionIconColors,
    getFieldSizeKey,
    getFieldColors,
    getFieldSize
} from "../fieldControl/stylesheet";
import DateTimeSheet from "../dateTimeSheet";
import FieldControl from "../fieldControl";
import StateLayer from "../stateLayer";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const CalendarFieldIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <CalendarIcon
    color={customColor}
    size={size}
/>;

const ClockFieldIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <ClockIcon
    color={customColor}
    size={size}
/>;

const LockFieldIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <LockIcon
    color={customColor}
    size={size}
/>;

const DateTimePickerIndicatorIcon: Record<DateTimePickerPickerType, NCoreUIKitIcon> = {
    "date-time": CalendarFieldIcon,
    date: CalendarFieldIcon,
    time: ClockFieldIcon
};

const preventActionFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

const DateTimePickerAction: FC<IFieldActionProps> = ({
    icon: IconComponentProp,
    accessibilityLabel,
    customIconColor,
    currentColors,
    currentSize,
    customTheme,
    isMultiline,
    isDisabled,
    isVisible,
    onPress,
    style
}) => {
    const {
        shapeConfigs,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: isVisible ?? true,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        customTheme
    });

    const pressScaleStyle = usePressScale({
        customTheme,
        isDisabled,
        isPressed
    });

    const appearStyle = useMemo(() => getAppearStyle({
        isReduceMotionEnabled,
        progress
    }), [
        isReduceMotionEnabled,
        progress
    ]);

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const currentIconColors = getFieldActionIconColors({
        isHighlighted: isHovered || isPressed,
        customIconColor,
        currentColors,
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = fieldActionStylesheet.useStyles({
        isFocusVisible,
        shapeConfigs,
        currentSize,
        isMultiline,
        radiuses,
        borders,
        colors
    });

    const webProps = Platform.OS === "web" ? {
        onMouseDown: preventActionFocusSteal
    } : {};

    if(!isRendered) {
        return null;
    }

    return <AnimatedPressable
        {...webProps}
        {...interactionHandlers}
        style={[
            containerStyle,
            fieldStylesheet.actionContainer,
            containerDynamicStyle,
            isVisible === false ? fieldStylesheet.hiddenActionContainer : undefined,
            isVisible === undefined ? undefined : appearStyle
        ]}
        aria-hidden={isVisible === false ? true : undefined}
        accessibilityLabel={accessibilityLabel}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onPress={onPress}
        role="button"
    >
        <Animated.View
            style={[
                surfaceStyle,
                fieldStylesheet.actionSurface,
                surfaceDynamicStyle,
                pressScaleStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.xs}
                customTheme={customTheme}
                overlayColor="default"
                isPressed={isPressed}
                isHovered={isHovered}
            />
            <IconComponentProp
                customColor={currentIconColors.iconColor}
                color={currentIconColors.iconColorKey}
                size={currentSize.iconSize}
            />
        </Animated.View>
    </AnimatedPressable>;
};

const DateTimePicker: RefForwardingComponent<IDateTimePickerRef, IDateTimePickerProps> = ({
    isTimeSelectWorksWithCenterWhenTimePicker = true,
    renderLoadingIcon: LoadingIconComponentProp,
    multipleSelectMinimumRequiredDayCount,
    isStartDateSelectionRequired = true,
    isEndDateSelectionRequired = true,
    rightIcon: RightIconComponentProp,
    localeBasedFirstDayOfWeek = true,
    monthOfYearLengthType = "short",
    isWorkWith24HoursFormat = true,
    multipleSelectMinimumRequireMS,
    spreadBehaviour = "baseline",
    customDateTimeSheetLocalize,
    isShowTodayIndicator = true,
    isShowDateTimeTools = true,
    isShowHintTextIcon = false,
    dayOfWeekLength = "short",
    isShowCalendarChangerTool,
    isShowCalenderChangerTool,
    isWorkWithRealtime = true,
    isWorkWithSeconds = false,
    customDateTimeSheetTheme,
    isShowWeekNumber = false,
    pickerType = "date-time",
    captionLayout = "label",
    icon: IconComponentProp,
    multipleSelectMaximumMS,
    hintTextContainerStyle,
    multipleSelectDayLimit,
    removeSelectValidation,
    contentContainerStyle,
    onFocus: onFocusProp,
    accessibilityLabel,
    dateTimeSheetProps,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    onBlur: onBlurProp,
    variant = "single",
    hintTextIconStyle,
    isLoading = false,
    dateCalendarType,
    initialDateRange,
    selectValidation,
    type = "neutral",
    initialDateRule,
    cleanIconStyle,
    clearIconStyle,
    customLocalize,
    dateRangeTitle,
    isCleanEnabled,
    isClearEnabled,
    isShowSubTitle,
    monthCount = 1,
    rightIconStyle,
    contentStyle,
    hintTextIcon,
    isSearchable,
    returnFormat,
    customTheme,
    initialDate,
    placeholder,
    titleFormat,
    isOptional,
    customKey,
    iconStyle,
    maxChoice,
    minChoice,
    hintText,
    onCancel,
    onChange,
    subTitle,
    maxDate,
    minDate,
    style,
    title,
    onOk,
    size,
    id
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        rruleConfig,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const readOnlyInputRef = useRef<DateTimePickerInputInstance | null>(null);

    const triggerRef = useRef<View>(null);

    const anchorRef = useMemo(() => ({
        get current() {
            return getDateTimePickerAnchor(triggerRef.current);
        }
    }), []);

    const dateTimePickerKey = useRef(customKey ? customKey : uuid());

    const generatedID = useRef(uuid());

    const previousIsActive = useRef(false);

    const activeCalendarTypeRef = useRef<DateCalendarType>(dateCalendarType ?? "day");

    const [
        isActive,
        setIsActive
    ] = useState(false);

    const [
        isSheetLoading,
        setIsSheetLoading
    ] = useState(false);

    const [
        isReadOnlyFocused,
        setIsReadOnlyFocused
    ] = useState(false);

    const [
        errorText,
        setErrorText
    ] = useState<string | undefined>(undefined);

    const [
        value,
        setValue
    ] = useState<DateTimePickerValueType>(() => ({
        dateRange: initialDateRange,
        dateRule: initialDateRule,
        date: initialDate
    }));

    const [
        draftValue,
        setDraftValue
    ] = useState<DateTimePickerValueType>(value);

    const inputID = id ?? generatedID.current;

    const valueID = `${inputID}-value`;

    const currentValue = isWorkWithRealtime || !isActive ? value : draftValue;

    const hasValue = getHasDateTimePickerValue({
        variant,
        value
    });

    const isCurrentClearEnabled = isClearEnabled ?? isCleanEnabled ?? false;

    const isCurrentShowCalendarChangerTool = isShowCalendarChangerTool ?? isShowCalenderChangerTool ?? true;

    const isHintTextIconStyleUsed = hintTextIconStyle !== undefined;

    const isCleanIconStyleUsed = cleanIconStyle !== undefined;

    const isInteractive = !isDisabled && !isReadOnly && !isLoading;

    const currentType = errorText ? "danger" : getDateTimePickerType({
        type
    });

    const currentSizeKey = getFieldSizeKey({
        isActionable: isCurrentClearEnabled,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getFieldColors({
        type: currentType,
        isDisabled,
        isReadOnly,
        colors
    });

    const currentTitleFormat = getDateTimePickerTitleFormat({
        isWorkWith24HoursFormat,
        isWorkWithSeconds,
        titleFormat,
        pickerType
    });

    const valueKey = getDateTimePickerValueKey(currentValue);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled: isDisabled || isReadOnly
    });

    const {
        trigger: triggerDynamicStyle
    } = useStyles({
        isLoading
    });

    const previousValueKey = useRef(valueKey);

    useImperativeHandle(
        ref,
        () => ({
            getValue: () => copyDateTimePickerValue(value),
            setDateRange: (dateRange) => handleApplyValue({
                dateRange
            }),
            setDateRule: (dateRule) => handleApplyValue({
                dateRule
            }),
            setDate: (date) => handleApplyValue({
                date
            }),
            setIsLoading: setIsSheetLoading,
            clear: handleClearAll,
            cancel: handleCancel,
            clean: handleClean,
            focus: handleFocus,
            blur: handleBlur,
            ok: handleOk
        })
    );

    useEffect(() => {
        if(previousIsActive.current === isActive) {
            return;
        }

        previousIsActive.current = isActive;

        if(isActive) {
            if(onFocusProp) {
                onFocusProp();
            }

            return;
        }

        if(onBlurProp) {
            onBlurProp();
        }
    }, [
        isActive
    ]);

    useEffect(() => {
        if(previousValueKey.current === valueKey) {
            return;
        }

        previousValueKey.current = valueKey;

        if(onChange) {
            onChange(getDateTimePickerResponse({
                value: currentValue,
                returnFormat
            }));
        }
    }, [
        valueKey
    ]);

    useEffect(() => {
        if(maxChoice === undefined || maxChoice === -1) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"maxChoice\" currently has no effect. The picker selects one value or one range."
        });
    }, [
        maxChoice
    ]);

    useEffect(() => {
        if(type !== "default") {
            return;
        }

        warnOnce({
            message: "DateTimePicker: type \"default\" is deprecated and will be removed in the next major version. Use \"neutral\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(type !== "question") {
            return;
        }

        warnOnce({
            message: "DateTimePicker: type \"question\" is deprecated and will be removed in the next major version. Use type \"neutral\" with \"isShowHintTextIcon\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(isShowCalenderChangerTool === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"isShowCalenderChangerTool\" is deprecated and will be removed in the next major version. Use \"isShowCalendarChangerTool\" instead."
        });
    }, [
        isShowCalenderChangerTool
    ]);

    useEffect(() => {
        if(isCleanEnabled === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"isCleanEnabled\" is deprecated and will be removed in the next major version. Use \"isClearEnabled\" instead."
        });
    }, [
        isCleanEnabled
    ]);

    useEffect(() => {
        if(!isCleanIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"cleanIconStyle\" is deprecated and will be removed in the next major version. Use \"clearIconStyle\" instead."
        });
    }, [
        isCleanIconStyleUsed
    ]);

    useEffect(() => {
        if(!isHintTextIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"hintTextIconStyle\" is deprecated and has no effect. The helper icon follows the field's helper row."
        });
    }, [
        isHintTextIconStyleUsed
    ]);

    useEffect(() => {
        if(isSearchable === undefined) {
            return;
        }

        warnOnce({
            message: "DateTimePicker: \"isSearchable\" is deprecated and has no effect."
        });
    }, [
        isSearchable
    ]);

    const handleApplyValue = (partialValue: DateTimePickerValueType) => {
        setErrorText(undefined);

        if(isWorkWithRealtime || !isActive) {
            setValue((previousValue) => ({
                ...previousValue,
                ...partialValue
            }));
        }

        if(!isWorkWithRealtime) {
            setDraftValue((previousValue) => ({
                ...previousValue,
                ...partialValue
            }));
        }
    };

    const handleFocusTrigger = () => {
        if(Platform.OS !== "web") {
            return;
        }

        triggerRef.current?.focus();
    };

    const handleFocusReadOnlyInput = () => {
        readOnlyInputRef.current?.focus();
    };

    const handleFocus = () => {
        if(isDisabled || isReadOnly) {
            return;
        }

        setErrorText(undefined);

        if(!isWorkWithRealtime) {
            setDraftValue(value);
        }

        setIsActive(true);
    };

    const handleBlur = () => {
        setIsActive(false);
    };

    const handleCancel = () => {
        if(isWorkWithRealtime) {
            return;
        }

        setErrorText(undefined);

        bottomSheetRef.current?.close(() => {
            handleBlur();
        });

        if(onCancel) {
            onCancel(getDateTimePickerResponse({
                returnFormat,
                value
            }));
        }
    };

    const handleDateCalendarTypeChange = (calendarType: DateCalendarType) => {
        activeCalendarTypeRef.current = calendarType;
    };

    const handleOk = (): boolean | void => {
        if(isWorkWithRealtime) {
            return;
        }

        if(variant === "range") {
            const rangeErrorKey = getDateTimePickerRangeError({
                calendarType: dateCalendarType ?? activeCalendarTypeRef.current,
                multipleSelectMinimumRequiredDayCount,
                dateRange: draftValue.dateRange,
                isStartDateSelectionRequired,
                isEndDateSelectionRequired,
                multipleSelectDayLimit
            });

            if(rangeErrorKey) {
                const rangeErrorText = localize(rangeErrorKey);

                NCoreUIKitToast.open({
                    title: rangeErrorText
                });

                setErrorText(rangeErrorText);

                return false;
            }
        }

        setErrorText(undefined);

        setValue(draftValue);

        bottomSheetRef.current?.close(() => {
            handleBlur();
        });

        if(onOk) {
            onOk(getDateTimePickerResponse({
                value: draftValue,
                returnFormat
            }));
        }
    };

    const handleClear = () => {
        handleApplyValue(DATE_TIME_PICKER_EMPTY_VALUE);
    };

    const handleClearAll = () => {
        setErrorText(undefined);

        setValue(DATE_TIME_PICKER_EMPTY_VALUE);

        setDraftValue(DATE_TIME_PICKER_EMPTY_VALUE);
    };

    const handleClean = () => {
        warnOnce({
            message: "DateTimePicker: ref method \"clean\" is deprecated and will be removed in the next major version. Use \"clear\" instead."
        });

        handleClearAll();
    };

    const handleSelectDateRule = (selectedDateRule?: RRule) => {
        if(variant !== "rrule") {
            return;
        }

        if(selectedDateRule && selectedDateRule !== currentValue.dateRule) {
            if(selectValidation && !selectValidation({
                dateRule: selectedDateRule
            })) {
                return;
            }

            handleApplyValue({
                dateRule: selectedDateRule
            });

            return;
        }

        if(removeSelectValidation && !removeSelectValidation({
            dateRule: currentValue.dateRule
        })) {
            return;
        }

        handleApplyValue({
            dateRule: undefined
        });
    };

    const handleRemoveDateRange = () => {
        if(minChoice || !currentValue.dateRange) {
            return;
        }

        if(removeSelectValidation && !removeSelectValidation({
            dateRange: currentValue.dateRange
        })) {
            return;
        }

        handleApplyValue({
            dateRange: undefined
        });
    };

    const handleRemoveDate = () => {
        if(minChoice || !currentValue.date) {
            return;
        }

        if(removeSelectValidation && !removeSelectValidation({
            date: currentValue.date
        })) {
            return;
        }

        handleApplyValue({
            date: undefined
        });
    };

    const handleSelectMultipleObject = (selectedDateRange?: DateTimePickerDateRange) => {
        if(variant !== "range") {
            return;
        }

        if(!selectedDateRange || !selectedDateRange.start && !selectedDateRange.end) {
            handleRemoveDateRange();

            return;
        }

        if(selectValidation && !selectValidation({
            dateRange: selectedDateRange
        })) {
            return;
        }

        handleApplyValue({
            dateRange: {
                start: selectedDateRange.start,
                end: selectedDateRange.end
            }
        });
    };

    const handleSelectObject = (pickedDate?: Date, isKeepTime?: boolean) => {
        if(variant !== "single") {
            return;
        }

        if(!pickedDate) {
            handleRemoveDate();

            return;
        }

        let selectedDate = pickedDate;

        if(isKeepTime && pickerType === "date-time" && currentValue.date) {
            const mergedDate = new Date(pickedDate);

            mergedDate.setHours(
                currentValue.date.getHours(),
                currentValue.date.getMinutes(),
                currentValue.date.getSeconds(),
                currentValue.date.getMilliseconds()
            );

            if(minDate && mergedDate < minDate) {
                selectedDate = new Date(minDate);
            } else if(maxDate && mergedDate > maxDate) {
                selectedDate = new Date(maxDate);
            } else {
                selectedDate = mergedDate;
            }
        }

        if(getIsSameDateTimePickerSelection({
            current: currentValue.date,
            next: selectedDate,
            isWorkWithSeconds,
            pickerType
        })) {
            if(pickerType !== "time") {
                handleRemoveDate();
            }

            return;
        }

        if(selectValidation && !selectValidation({
            date: selectedDate
        })) {
            return;
        }

        handleApplyValue({
            date: selectedDate
        });
    };

    const onClearPress = () => {
        handleClearAll();

        handleFocusTrigger();
    };

    const onTriggerPress = () => {
        if(!isInteractive) {
            return;
        }

        handleFocusTrigger();

        handleFocus();
    };

    const onReadOnlyFocus = () => {
        setIsReadOnlyFocused(true);
    };

    const onReadOnlyBlur = () => {
        setIsReadOnlyFocused(false);
    };

    const getDisplayText = () => {
        if(variant === "rrule") {
            return value.dateRule?.toText(undefined, parseRRuleConfig(rruleConfig));
        }

        if(variant === "range") {
            if(!hasValue) {
                return undefined;
            }

            if(dateRangeTitle) {
                return dateRangeTitle(value.dateRange);
            }

            return getDateTimePickerRangeText({
                dateRange: value.dateRange,
                format: currentTitleFormat,
                activeLocale
            }) || undefined;
        }

        return getDateTimePickerDateText({
            format: currentTitleFormat,
            date: value.date,
            activeLocale
        }) || undefined;
    };

    const getPlaceholder = () => {
        return placeholder ?? localize(getDateTimePickerPlaceholderKey({
            pickerType,
            variant
        }));
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const getFocusRequest = () => {
        if(isReadOnly) {
            return handleFocusReadOnlyInput;
        }

        return isInteractive ? onTriggerPress : undefined;
    };

    const getStartAction = (): FieldActionType | undefined => {
        if(!IconComponentProp) {
            return undefined;
        }

        return {
            icon: IconComponentProp,
            style: iconStyle
        };
    };

    const getEndAction = (): FieldActionType => {
        if(isReadOnly) {
            return {
                icon: LockFieldIcon
            };
        }

        return {
            icon: RightIconComponentProp ?? DateTimePickerIndicatorIcon[pickerType],
            style: rightIconStyle
        };
    };

    const getTriggerAccessibilityProps = (accessibilityProps: FieldInputAccessibilityType, displayText?: string): DateTimePickerTriggerAccessibilityType => {
        if(Platform.OS === "web") {
            return {
                ...accessibilityProps,
                "aria-labelledby": accessibilityProps["aria-labelledby"] ? `${accessibilityProps["aria-labelledby"]} ${valueID}` : undefined,
                "aria-haspopup": "dialog"
            };
        }

        if(Platform.OS !== "ios" || !title) {
            return accessibilityProps;
        }

        return {
            ...accessibilityProps,
            accessibilityValue: {
                text: displayText ?? getPlaceholder()
            }
        };
    };

    const renderClearAction = () => {
        if(!isCurrentClearEnabled || !isInteractive || minChoice) {
            return null;
        }

        return <DateTimePickerAction
            accessibilityLabel={localize("clear-selection")}
            style={clearIconStyle ?? cleanIconStyle}
            currentColors={currentColors}
            currentSize={currentSize}
            customTheme={customTheme}
            onPress={onClearPress}
            icon={FieldClearIcon}
            isVisible={hasValue}
            isMultiline={false}
            isDisabled={false}
        />;
    };

    const renderTrigger = ({
        style: inputDynamicStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        const displayText = getDisplayText();

        if(isReadOnly) {
            return <NativeTextInput
                {...accessibilityProps}
                placeholderTextColor={placeholderTextColor}
                underlineColorAndroid="transparent"
                placeholder={getPlaceholder()}
                style={[
                    stylesheet.readOnlyInput,
                    inputDynamicStyle
                ]}
                onFocus={onReadOnlyFocus}
                value={displayText ?? ""}
                onBlur={onReadOnlyBlur}
                ref={readOnlyInputRef}
                multiline={false}
                editable={false}
            />;
        }

        return <Fragment>
            <Pressable
                {...getTriggerAccessibilityProps(accessibilityProps, displayText)}
                {...interactionHandlers}
                onPress={onTriggerPress}
                aria-expanded={isActive}
                style={[
                    contentStyle,
                    stylesheet.trigger,
                    triggerDynamicStyle
                ]}
                disabled={isDisabled}
                ref={triggerRef}
                role="button"
            >
                <Text
                    customColor={displayText ? currentColors.valueColor : placeholderTextColor}
                    variant={currentSize.valueVariant}
                    customTheme={customTheme}
                    style={stylesheet.value}
                    numberOfLines={1}
                    id={valueID}
                >
                    {displayText ?? getPlaceholder()}
                </Text>
            </Pressable>
            {renderClearAction()}
        </Fragment>;
    };

    return <Fragment>
        <FieldControl
            isShowHintTextIcon={isShowHintTextIcon || type === "question"}
            isFocused={isActive || isFocusVisible || isReadOnlyFocused}
            hintTextContainerStyle={hintTextContainerStyle}
            accessibilityLabel={accessibilityLabel}
            controlStyle={contentContainerStyle}
            onFocusRequest={getFocusRequest()}
            spreadBehaviour={spreadBehaviour}
            hintText={errorText ?? hintText}
            startAction={getStartAction()}
            hintTextIcon={hintTextIcon}
            renderInput={renderTrigger}
            endAction={getEndAction()}
            customTheme={customTheme}
            subTitle={getSubTitle()}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            isRequired={isRequired}
            size={currentSizeKey}
            isLoading={isLoading}
            type={currentType}
            inputID={inputID}
            title={title}
            style={style}
        />
        <DateTimeSheet
            isTimeSelectWorksWithCenterWhenTimePicker={isTimeSelectWorksWithCenterWhenTimePicker}
            multipleSelectMinimumRequiredDayCount={multipleSelectMinimumRequiredDayCount}
            multipleSelectMinimumRequireMS={multipleSelectMinimumRequireMS}
            customLocalize={customDateTimeSheetLocalize ?? customLocalize}
            isShowCalendarChangerTool={isCurrentShowCalendarChangerTool}
            onDateCalendarTypeChange={handleDateCalendarTypeChange}
            customTheme={customDateTimeSheetTheme ?? customTheme}
            localeBasedFirstDayOfWeek={localeBasedFirstDayOfWeek}
            LoadingIconComponentProp={LoadingIconComponentProp}
            isWorkWith24HoursFormat={isWorkWith24HoursFormat}
            multipleSelectMaximumMS={multipleSelectMaximumMS}
            selectMultipleObject={handleSelectMultipleObject}
            multipleSelectDayLimit={multipleSelectDayLimit}
            dateTimePickerKey={dateTimePickerKey.current}
            monthOfYearLengthType={monthOfYearLengthType}
            isShowTodayIndicator={isShowTodayIndicator}
            isWorkWithRealtime={isWorkWithRealtime}
            bottomSheetProps={dateTimeSheetProps}
            selectDateRule={handleSelectDateRule}
            isWorkWithSeconds={isWorkWithSeconds}
            isShowWeekNumber={isShowWeekNumber}
            dateCalendarType={dateCalendarType}
            dateRange={currentValue.dateRange}
            selectObject={handleSelectObject}
            isShowTools={isShowDateTimeTools}
            dayOfWeekLength={dayOfWeekLength}
            dateRule={currentValue.dateRule}
            bottomSheetRef={bottomSheetRef}
            captionLayout={captionLayout}
            isLoading={isSheetLoading}
            setIsActive={setIsActive}
            date={currentValue.date}
            monthCount={monthCount}
            pickerType={pickerType}
            anchorRef={anchorRef}
            minChoice={minChoice}
            cancel={handleCancel}
            clear={handleClear}
            isOpen={isActive}
            variant={variant}
            maxDate={maxDate}
            minDate={minDate}
            ok={handleOk}
        />
    </Fragment>;
};
export default forwardRef(DateTimePicker);
